import moment from 'moment-timezone'
import * as R from 'ramda'
import { DataPoint } from '@/jsonapi/CommonTypes'
import { USER_TIME_ZONE } from '@/utils/time'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'

export interface BarChartOptions {
  timezone: string
  xAxisLabels: string[]
  seriesXData: Array<[string, number]>
  threshold: number
  markLines?: number[]
  tooltipFormatter: echarts.EChartOption.Tooltip.Formatter
  baseColor: string
  lowValueColor: string
  minYValue: number
  maxYValue: number
  options?: Record<string, any>
}

export function* fillDate(timezone: string, startTimestamp: number, endTimestamp: number) {
  let currentDate = moment(startTimestamp)
    .tz(timezone || USER_TIME_ZONE)
    .startOf('day')
    .valueOf()
  while (currentDate <= endTimestamp) {
    yield currentDate
    currentDate = moment(currentDate)
      .tz(timezone || USER_TIME_ZONE)
      .add(1, 'day')
      .startOf('day')
      .valueOf()
  }
}

export function fillDateForObjKeys<T>(dates: number[] | string[], obj, def: any = 0): { [key: string]: T } {
  const clone = R.clone(obj)
  dates.forEach(date => {
    if (!clone[String(date)]) {
      clone[String(date)] = def
    }
  })
  return clone
}

export function buildDataPointsByDay(dataPoints: DataPoint[], timezone): { [key: string]: number } {
  const dataPointsMapByDay: { [key: string]: number } = {}
  dataPoints.forEach(dataPoint => {
    const today = moment(dataPoint.timestamp)
      .tz(timezone)
      .startOf('day')
      .valueOf()
    if (!dataPointsMapByDay[today]) {
      dataPointsMapByDay[today] = 0
    }
    dataPointsMapByDay[today] = dataPointsMapByDay[today] + dataPoint.value
  })
  return dataPointsMapByDay
}

export function buildDataPointsByDayWithNumOfItems(dataPoints: DataPoint[], timezone): { [key: string]: number } {
  const dataPointsMapByDay: { [key: string]: number } = {}
  dataPoints.forEach(dataPoint => {
    const today = moment(dataPoint.timestamp)
      .tz(timezone)
      .startOf('day')
      .valueOf()
    if (!dataPointsMapByDay[today]) {
      dataPointsMapByDay[today] = 0
    }
    dataPointsMapByDay[today] = dataPointsMapByDay[today] + 1
  })
  return dataPointsMapByDay
}

export function buildDataPointsByDayWithRawData(
  dataPoints: DataPoint[],
  timezone
): { [key: string]: { value: number; rawData: any[] } } {
  const dataPointsMapByDay: { [key: string]: { value: number; rawData: any[] } } = {}
  dataPoints.forEach(dataPoint => {
    const today = moment(dataPoint.timestamp)
      .tz(timezone)
      .startOf('day')
      .valueOf()
    if (!dataPointsMapByDay[today]) {
      dataPointsMapByDay[today] = {
        value: 0,
        rawData: [],
      }
    }
    dataPointsMapByDay[today].value = dataPointsMapByDay[today].value + dataPoint.value
    dataPointsMapByDay[today].rawData.push(dataPoint)
  })
  return dataPointsMapByDay
}

export function buildBarChartOptions(options: BarChartOptions): echarts.EChartOption<echarts.EChartOption.SeriesBar> {
  return {
    ...options.options,
    xAxis: {
      data: options.xAxisLabels,
      type: 'category',
      axisLine: {
        show: true,
        lineStyle: {
          color: 'rgba(6, 64, 80, 0.3)', // 0f45cd
          width: 2,
        },
      },
      axisTick: {
        show: false,
        alignWithLabel: true,
      },
      splitLine: { show: false },
      axisLabel: {
        show: true,
        showMinLabel: true,
        showMaxLabel: true,
        interval: options.xAxisLabels.length - 1,
        formatter: value =>
          moment(Number(value))
            .tz(options.timezone)
            .format(CHART_DATE_FORMAT.DATE),
      },
    },
    yAxis: {
      type: 'value',
      splitNumber: 1,
      axisLine: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      min: options.minYValue,
      max: options.maxYValue,
    },
    series: [
      {
        type: 'bar',
        label: { show: false },
        itemStyle: {
          color: options.baseColor,
          barBorderRadius: [4, 4, 0, 0],
        },
        // @ts-ignore
        data: options.seriesXData.map(xData => ({
          value: xData,
          itemStyle: {
            color: xData[1] <= options.threshold ? options.lowValueColor : options.baseColor,
          },
        })),
        markLine: {
          silent: true,
          precision: 0,
          symbol: [],
          label: {
            position: 'start',
            formatter: params => {
              const data = params.value
              if (!Number.isInteger(data)) {
                return data
              }
              if (data >= 1000) {
                return `${data * 0.001}k`
              } else {
                return data
              }
            },
          },
          lineStyle: {
            color: '#0f45cd',
            opacity: 0.2,
            type: 'solid',
          },
          // @ts-ignore
          data: (options.markLines || [options.threshold]).map(num => ({ yAxis: num })),
        },
      },
    ],
    tooltip: {
      formatter: options.tooltipFormatter,
    },
  }
}
